/**
 * Read-only "what the OCR detected" hint.
 *
 * Textract/extraction writes the raw detected text into the Excel-side fields
 * (e.g. `vendorExcel`, `customerExcel`). Those frequently differ from the
 * QBO/Xero contact the user maps the invoice to (the OCR grabs whatever string
 * is printed on the page — a buyer header, a "remit to" line, an abbreviation —
 * which may not match the bookkeeping vendor name). When the user is looking at
 * the QBO/Xero mapping it's easy to lose sight of what was actually on the
 * document, so we surface the original detected value right under the field as a
 * dimmed, non-interactive reference.
 *
 * Renders nothing when there's no detected value, or when the detected value is
 * identical to what's already shown in the mapped field (no point echoing it).
 */
import React from "react";

type Props = {
  /** What the field maps to, e.g. "Vendor", "Customer", "Supplier". */
  label: string;
  /**
   * The raw OCR-detected value (vendorExcel / customerExcel / etc.). The
   * extracted invoice blob is stored verbatim with no type whitelist, so this
   * can arrive as a number (or other non-string) — coerce before string ops.
   */
  detected?: string | number | null;
  /** The label currently shown in the mapped select, to suppress echoes. */
  mappedLabel?: string | number | null;
};

const norm = (s?: string | number | null) =>
  (s == null ? "" : String(s)).trim().toLowerCase();

const OcrDetectedHint: React.FC<Props> = ({ label, detected, mappedLabel }) => {
  const value = (detected == null ? "" : String(detected)).trim();
  if (!value) return null;
  // Don't echo a value the mapped field already displays verbatim.
  if (norm(value) === norm(mappedLabel)) return null;

  return (
    <div
      className="text-muted mt-1"
      style={{ fontSize: "0.75rem", lineHeight: 1.2 }}
      title={`The text the document scanner detected for ${label.toLowerCase()}. The mapped value above is what syncs to your accounting integration.`}
    >
      Detected by OCR: <span style={{ fontStyle: "italic" }}>{value}</span>
    </div>
  );
};

export default OcrDetectedHint;
